import PersonIcon from '@mui/icons-material/Person';
import { Auth } from 'aws-amplify';
import { pageRoutes } from '../../constants/routes';
import { clearSessionActiveOrganizationId } from '../../utils/activeOrganizationStorage';
import { pxToRem } from '../../utils/getFontValue';
import { setShowNotification } from '../../redux/slice/snackBarSlice';
import { styled } from '@mui/material/styles';
import { UIColors } from '../../constants/UIConstants';
import { useAppDispatch } from '../../redux/store';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import OrgSwitcher from './OrgSwitcher';
import {
  AppBar,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';

const HEADER_CONTROL_HEIGHT = pxToRem(40);

const StyledAppBar = styled(AppBar)`
  background-color: ${({ theme }) => theme.palette.primary.main};
`;

export const StyledContainer = styled(Container)`
  @media (min-width: ${({ theme }) => theme.breakpoints.values.lg}px) {
    padding: 0 ${pxToRem(46)};
    max-width: unset;
  }
`;

const StyledToolbar = styled(Toolbar)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 5.438rem;
`;

const AppTitle = styled(Typography)`
  margin-right: ${(props) => props.theme.spacing(2)};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-weight: 700;
  font-size: 1.5rem;
  white-space: nowrap;
  line-height: 140%;
  text-overflow: ellipsis;
  overflow: hidden;
  color: inherit;
`;

const BrandButton = styled('button')`
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: inherit;
  font: inherit;
  text-align: left;

  &:hover {
    opacity: 0.92;
  }
`;

const StyledImgIcon = styled('img')`
  width: 53px;
  height: 67px;
  object-fit: cover;
`;

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [openSignOut, setOpenSignOut] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpenSignOut(true);
  };

  const handleSignOut = async () => {
    try {
      clearSessionActiveOrganizationId();
      await Auth.signOut();
      setOpenSignOut(false);
      navigate(pageRoutes.rootPath);
    } catch (error) {
      dispatch(setShowNotification('Failed to sign out. Please try again'));
    }
  };

  return (
    <StyledAppBar position="static">
      <StyledContainer>
        <StyledToolbar disableGutters>
          <BrandButton
            type="button"
            onClick={() => navigate(pageRoutes.onboarding)}
            aria-label="Go to onboarding home"
          >
            <StyledImgIcon src="/kickdrum_logo_192.png" alt="" />
            <AppTitle variant="h6">Rapid Performance Testing</AppTitle>
          </BrandButton>

          {location.pathname !== pageRoutes.rootPath && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexShrink: 0,
              }}
            >
              <OrgSwitcher appearance="appHeader" />
              <IconButton
                onClick={handleAvatarClick}
                aria-label="Open account menu"
                aria-haspopup="true"
                aria-expanded={openSignOut ? 'true' : undefined}
                sx={{
                  width: HEADER_CONTROL_HEIGHT,
                  height: HEADER_CONTROL_HEIGHT,
                  p: 0,
                  flexShrink: 0,
                  borderRadius: pxToRem(8),
                  border: '1px solid rgba(255, 255, 255, 0.35)',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: UIColors.OnBg.white,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    borderColor: 'rgba(255, 255, 255, 0.55)',
                  },
                }}
              >
                <PersonIcon sx={{ fontSize: pxToRem(22), color: 'inherit' }} />
              </IconButton>
              <Menu
                keepMounted
                open={openSignOut}
                anchorEl={anchorEl}
                onClose={() => setOpenSignOut(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: pxToRem(140),
                  },
                }}
              >
                <MenuItem onClick={handleSignOut} sx={{ fontSize: pxToRem(14) }}>
                  Sign Out
                </MenuItem>
              </Menu>
            </Box>
          )}
        </StyledToolbar>
      </StyledContainer>
    </StyledAppBar>
  );
}
export default Header;
